import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, take } from 'rxjs';
import { AuthService } from '../../auth.service';
import { PickupCreated, PickupItem, PickupLocation, PickupRequest, PickupTemplate } from './pickup.models';
import { PickupService } from './pickup.service';

type StopRole = 'shipper' | 'consignee';
interface StopState {
  location: PickupLocation | null;
  editing: boolean;
  query: string;
  searching: boolean;
  results: PickupLocation[];
  error: string;
}
interface PendingPickup {
  key: string;
  request: PickupRequest;
}

@Component({
  selector: 'app-pickup-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './pickup-dialog.component.html'
})
export class PickupDialogComponent implements OnInit, OnDestroy {
  template: PickupTemplate | null = null;
  loading = true;
  saving = false;
  error = '';
  pending: PendingPickup | null = null;
  manualMode = false;
  filter = '';
  selected = new Map<string, number>();
  readonly roles: StopRole[] = ['shipper', 'consignee'];
  readonly stops: Record<StopRole, StopState> = {
    shipper: { location: null, editing: false, query: '', searching: false, results: [], error: '' },
    consignee: { location: null, editing: false, query: '', searching: false, results: [], error: '' }
  };
  form = {
    purpose: 'pickup' as PickupRequest['purpose'], pickup_date: '', pickup_time: '',
    delivery_date: '', delivery_time: '', reference: '', instructions: '', weight_lb: null as number | null,
    description: '', quantity: 1
  };
  private storageKey = '';
  private readonly searches: Partial<Record<StopRole, Subscription>> = {};
  private readonly subscriptions = new Subscription();
  private templateRequest?: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: { ladingUuid: string },
    private readonly dialog: MatDialogRef<PickupDialogComponent, PickupCreated>,
    private readonly api: PickupService,
    private readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.auth.loadCurrentUser().pipe(take(1)).subscribe({
      next: user => {
        this.storageKey = `pickup-pending:${user?.user_id}:${this.data.ladingUuid}`;
        this.restorePending();
        this.reload();
      },
      error: error => { this.error = this.message(error); this.loading = false; }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.templateRequest?.unsubscribe();
    this.searches.shipper?.unsubscribe();
    this.searches.consignee?.unsubscribe();
  }

  reload(): void {
    this.templateRequest?.unsubscribe();
    this.loading = true;
    this.error = '';
    this.templateRequest = this.api.template(this.data.ladingUuid).subscribe({
      next: result => {
        this.template = result;
        this.stops.shipper.location = result.shipper;
        this.stops.consignee.location = result.consignee;
        this.selected.clear();
        this.manualMode = result.items.length === 0;
        this.loading = false;
      },
      error: error => { this.error = this.message(error); this.loading = false; }
    });
  }

  search(role: StopRole): void {
    const stop = this.stops[role];
    this.searches[role]?.unsubscribe();
    stop.results = [];
    stop.error = '';
    if (stop.query.trim().length < 2) return;
    stop.searching = true;
    this.searches[role] = this.api.locations(this.data.ladingUuid, stop.query.trim()).subscribe({
      next: results => { stop.results = results; stop.searching = false; },
      error: error => { stop.error = this.message(error); stop.searching = false; }
    });
  }

  choose(role: StopRole, location: PickupLocation): void {
    this.stops[role].location = location;
    this.stops[role].editing = false;
    this.stops[role].results = [];
  }

  address(location: PickupLocation): string {
    return [location.address_street, location.address_extended,
      [location.address_locality, location.address_region, location.address_postal_code].filter(Boolean).join(' '),
      location.address_country].filter(Boolean).join(', ');
  }

  get visibleItems(): PickupItem[] {
    const query = this.filter.trim().toLowerCase();
    return (this.template?.items ?? []).filter(item => !query ||
      [item.description, item.model_number, item.serial_number, item.barcode, item.package_id].join(' ').toLowerCase().includes(query));
  }

  itemKey(item: PickupItem): string { return `${item.kind}:${item.uuid}`; }

  toggle(item: PickupItem, checked: boolean): void {
    if (checked) this.selected.set(this.itemKey(item), 1);
    else this.selected.delete(this.itemKey(item));
  }

  quantity(item: PickupItem, value: number): void { this.selected.set(this.itemKey(item), Number(value)); }

  selectVisible(): void {
    for (const item of this.visibleItems) {
      if (item.available_quantity > 0) this.selected.set(this.itemKey(item), 1);
    }
  }

  get pieces(): number {
    return this.manualMode ? Number(this.form.quantity) : [...this.selected.values()].reduce((sum, quantity) => sum + quantity, 0);
  }

  save(form: NgForm): void {
    if (this.saving) return;
    this.error = '';
    if (!this.pending) {
      form.control.markAllAsTouched();
      if (form.invalid || !this.template || !this.stops.shipper.location || !this.stops.consignee.location) {
        this.error = 'Choose both locations, a pickup date, and the items to collect.';
        return;
      }
      if (!this.manualMode && this.selected.size === 0) { this.error = 'Select at least one item.'; return; }
      const items = (this.template.items ?? []).filter(item => this.selected.has(this.itemKey(item))).map(item => ({
        kind: item.kind, uuid: item.uuid, quantity: this.selected.get(this.itemKey(item))!
      }));
      if (items.some(item => !Number.isInteger(item.quantity) || item.quantity < 1 ||
          item.quantity > this.template!.items.find(candidate => candidate.uuid === item.uuid && candidate.kind === item.kind)!.available_quantity)) {
        this.error = 'Check the selected quantities.';
        return;
      }
      const party = (role: StopRole) => ({ contact_uuid: this.stops[role].location!.contact_uuid,
        contact_address_uuid: this.stops[role].location!.contact_address_uuid });
      this.pending = { key: crypto.randomUUID(), request: {
        source_version: this.template.source_version, purpose: this.form.purpose,
        shipper: party('shipper'), consignee: party('consignee'),
        pickup_date: this.form.pickup_date, pickup_time: this.form.pickup_time || null,
        delivery_date: this.form.delivery_date || null, delivery_time: this.form.delivery_time || null,
        reference: this.form.reference.trim() || null, instructions: this.form.instructions.trim() || null,
        weight_lb: this.form.weight_lb,
        items: this.manualMode ? [] : items,
        manual: this.manualMode ? { description: this.form.description.trim(), quantity: Number(this.form.quantity) } : null
      }};
      this.storePending();
    }
    this.saving = true;
    this.dialog.disableClose = true;
    this.subscriptions.add(this.api.create(this.data.ladingUuid, this.pending.request, this.pending.key).subscribe({
      next: result => {
        this.saving = false;
        this.dialog.disableClose = false;
        if (!result.ok || !result.data?.lading_uuid) {
          this.error = 'Creation could not be confirmed. Retry the saved request.';
          return;
        }
        this.clearPending();
        this.dialog.close(result.data);
      },
      error: error => {
        this.error = this.message(error);
        if (error?.status >= 400 && error?.status < 500 && !error?.error?.retryable && error?.error?.error !== 'idempotency_in_progress') {
          this.clearPending();
        }
        this.saving = false;
        this.dialog.disableClose = false;
      }
    }));
  }

  close(): void { if (!this.saving) this.dialog.close(); }

  private restorePending(): void {
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (saved) this.pending = JSON.parse(saved) as PendingPickup;
    } catch { this.pending = null; }
  }

  private storePending(): void {
    try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.pending)); } catch { /* Retry remains available in this dialog. */ }
  }

  private clearPending(): void {
    this.pending = null;
    try { sessionStorage.removeItem(this.storageKey); } catch { /* Storage may be disabled. */ }
  }

  private message(error: any): string {
    return error?.error?.message || error?.message || 'The pickup request could not be completed.';
  }
}
